/**
 * ProductDataGrid Component
 * Inline editing table for geogrid and drainage products
 */

import React, { useCallback, useState } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    IconButton,
    Tooltip,
    Chip,
    styled,
    TableSortLabel,
    TablePagination,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    FormHelperText,
    OutlinedInput,
    SelectChangeEvent,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import { IAdminGeogridProduct, IAdminGeogridProductRequest, APPLICABLE_PROJECT_TYPES } from '../types/AdminTypes';

// Styled components
const StyledTableContainer = styled(TableContainer)(() => ({
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    overflow: 'auto',
    maxHeight: 'calc(100vh - 350px)',
    width: '100%',
}));

const StyledTableHead = styled(TableHead)(() => ({
    '& .MuiTableCell-head': {
        fontWeight: 'bold',
        fontSize: '0.8rem',
        padding: '10px 6px',
        borderRight: '1px solid rgba(255,255,255,0.2)',
        color: 'white',
        backgroundColor: '#004652', // Maccaferri brand green - must be on cell for stickyHeader to work
        '&:last-child': {
            borderRight: 'none',
        },
        // Ensure TableSortLabel inherits white color
        '& .MuiTableSortLabel-root': {
            color: 'white',
            '&:hover': {
                color: 'rgba(255,255,255,0.8)',
            },
            '&.Mui-active': {
                color: 'white',
                '& .MuiTableSortLabel-icon': {
                    color: 'white',
                },
            },
        },
    },
}));

const StyledTableRow = styled(TableRow)<{ editing?: number }>(({ editing }) => ({
    backgroundColor: editing ? '#fff8e1' : 'inherit',
    '&:hover': {
        backgroundColor: editing ? '#fff8e1' : '#f5f5f5',
    },
    '& .MuiTableCell-body': {
        padding: '6px',
        borderRight: '1px solid #e0e0e0',
        fontSize: '0.8rem',
        '&:last-child': {
            borderRight: 'none',
        },
    },
}));

const EditableCell = styled(TableCell)(() => ({
    '& .MuiTextField-root': {
        '& input': {
            padding: '4px 6px',
            fontSize: '0.8rem',
        },
    },
}));

interface ProductDataGridProps {
    products: IAdminGeogridProduct[];
    onUpdate: (id: string, product: IAdminGeogridProductRequest) => Promise<void>;
    onDelete: (id: string) => Promise<void>;
    loading?: boolean;
}

type Order = 'asc' | 'desc';

export const ProductDataGrid: React.FC<ProductDataGridProps> = ({ products, onUpdate, onDelete, loading }) => {
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editedProduct, setEditedProduct] = useState<Partial<IAdminGeogridProduct>>({});
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<keyof IAdminGeogridProduct>('family');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const handleRequestSort = (property: keyof IAdminGeogridProduct) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleStartEdit = useCallback((product: IAdminGeogridProduct) => {
        setEditingId(product.id);
        setEditedProduct({ ...product });
    }, []);

    const handleCancelEdit = useCallback(() => {
        setEditingId(null);
        setEditedProduct({});
    }, []);

    const handleSaveEdit = useCallback(async () => {
        if (!editingId || !editedProduct) return;

        // S9-2: Validate LCR fields are either all filled or all empty
        const lcrFields = [editedProduct.lcrEquationM2, editedProduct.lcrEquationM1, editedProduct.lcrEquationC, editedProduct.lcrEquationThreshold];
        const filledLcrCount = lcrFields.filter(v => v != null).length;
        if (filledLcrCount > 0 && filledLcrCount < 4) {
            alert('LCR equation requires all 4 fields (M2, M1, C, Threshold) or none. Partial data will be discarded.');
            return;
        }

        const request: IAdminGeogridProductRequest = {
            prodotto: editedProduct.prodotto || '',
            family: editedProduct.family || '',
            familyId: editedProduct.familyId || '',
            category: editedProduct.category || 'GEOGRID',
            tensile: editedProduct.tensile,
            tensileMax: editedProduct.tensileMax,
            tensile1Percent: editedProduct.tensile1Percent,
            tensile2Percent: editedProduct.tensile2Percent,
            tensile3Percent: editedProduct.tensile3Percent,
            tensile4Percent: editedProduct.tensile4Percent,
            tensile5Percent: editedProduct.tensile5Percent,
            torsionalStiffness: editedProduct.torsionalStiffness,
            applicableProjects: editedProduct.applicableProjects,
            applicableRegions: editedProduct.applicableRegions,
            lcrEquationM2: editedProduct.lcrEquationM2,
            lcrEquationM1: editedProduct.lcrEquationM1,
            lcrEquationC: editedProduct.lcrEquationC,
            lcrEquationThreshold: editedProduct.lcrEquationThreshold,
            lcrBase: editedProduct.lcrBase,
            lcrSubbase: editedProduct.lcrSubbase,
            grade: editedProduct.grade,
            drainageCoefficientOverride: editedProduct.drainageCoefficientOverride,
            databaseReference: editedProduct.databaseReference,
        };

        try {
            await onUpdate(editingId, request);
            setEditingId(null);
            setEditedProduct({});
        } catch (error) {
            console.error('Error saving product:', error);
        }
    }, [editingId, editedProduct, onUpdate]);

    const handleFieldChange = useCallback((field: keyof IAdminGeogridProduct, value: string | number | null) => {
        setEditedProduct(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleDelete = useCallback(
        async (id: string) => {
            if (window.confirm('Are you sure you want to delete this product?')) {
                await onDelete(id);
            }
        },
        [onDelete]
    );

    // Sorting
    const sortedProducts = [...products].sort((a, b) => {
        const aValue = a[orderBy];
        const bValue = b[orderBy];

        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return order === 'asc' ? aValue - bValue : bValue - aValue;
        }

        return 0;
    });

    // Pagination
    const paginatedProducts = sortedProducts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const renderCell = (product: IAdminGeogridProduct, field: keyof IAdminGeogridProduct, type: 'text' | 'number', decimals = 2, step = 0.1) => {
        const isEditing = editingId === product.id;
        const value = isEditing ? editedProduct[field] : product[field];

        if (isEditing) {
            return (
                <TextField
                    size="small"
                    type={type}
                    value={value ?? ''}
                    onChange={e => {
                        const newValue = type === 'number' ? (e.target.value === '' ? null : Number(e.target.value)) : e.target.value;
                        handleFieldChange(field, newValue);
                    }}
                    variant="outlined"
                    fullWidth
                    inputProps={type === 'number' ? { step, min: 0 } : {}}
                />
            );
        }

        if (type === 'number' && value !== null && value !== undefined) {
            return (value as number).toFixed(decimals);
        }

        return value ?? '-';
    };

    const renderProjectChipsView = (projects: string[] | null | undefined) => {
        if (!projects || projects.length === 0) {
            return <span style={{ color: '#999' }}>None</span>;
        }
        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {projects.slice(0, 3).map(project => (
                    <Chip key={project} label={project.substring(0, 3).toUpperCase()} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                ))}
                {projects.length > 3 && <Chip label={`+${projects.length - 3}`} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />}
            </Box>
        );
    };

    const renderProjectsEditor = (selectedProjects: string[]) => {
        const handleProjectsChange = (event: SelectChangeEvent<string[]>) => {
            const { value } = event.target;
            const nextProjects = typeof value === 'string' ? value.split(',') : value;
            setEditedProduct(prev => ({ ...prev, applicableProjects: nextProjects }));
        };
        return (
            <FormControl fullWidth size="small">
                <InputLabel id="projects-editor-label">Projects</InputLabel>
                <Select
                    labelId="projects-editor-label"
                    id="projects-editor-select"
                    multiple
                    value={selectedProjects}
                    onChange={handleProjectsChange}
                    input={<OutlinedInput label="Projects" />}
                    renderValue={selected => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map(value => (
                                <Chip key={value} label={value.substring(0, 3).toUpperCase()} size="small" sx={{ fontSize: '0.65rem', height: 20 }} />
                            ))}
                        </Box>
                    )}>
                    {APPLICABLE_PROJECT_TYPES.map(project => (
                        <MenuItem key={project} value={project}>
                            {project.replace('_', ' ').toUpperCase()}
                        </MenuItem>
                    ))}
                </Select>
                {selectedProjects.length === 0 && <FormHelperText sx={{ color: 'warning.main', mt: 0.5, lineHeight: 1.2 }}>May be hidden from project-type filters when empty</FormHelperText>}
            </FormControl>
        );
    };

    return (
        <Box>
            <Paper
                sx={{
                    overflow: 'hidden',
                    // Override global compact-input rule (style/mui.scss) inside edit rows
                    // so multi-select labels (Projects) can center correctly.
                    '& .MuiOutlinedInput-root': { height: 'auto !important' },
                }}>
                <StyledTableContainer>
                    <Table stickyHeader size="small">
                        <StyledTableHead>
                            <TableRow>
                                <TableCell sx={{ minWidth: 60, width: 60 }}>Actions</TableCell>
                                <TableCell sx={{ minWidth: 100 }}>
                                    <TableSortLabel active={orderBy === 'id'} direction={orderBy === 'id' ? order : 'asc'} onClick={() => handleRequestSort('id')}>
                                        ID
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ minWidth: 150 }}>
                                    <TableSortLabel active={orderBy === 'prodotto'} direction={orderBy === 'prodotto' ? order : 'asc'} onClick={() => handleRequestSort('prodotto')}>
                                        Product Name
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ minWidth: 120 }}>
                                    <TableSortLabel active={orderBy === 'family'} direction={orderBy === 'family' ? order : 'asc'} onClick={() => handleRequestSort('family')}>
                                        Family
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ minWidth: 80 }}>
                                    <TableSortLabel active={orderBy === 'category'} direction={orderBy === 'category' ? order : 'asc'} onClick={() => handleRequestSort('category')}>
                                        Category
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ minWidth: 60 }}>T 1%</TableCell>
                                <TableCell sx={{ minWidth: 60 }}>T 2%</TableCell>
                                <TableCell sx={{ minWidth: 60 }}>T 3%</TableCell>
                                <TableCell sx={{ minWidth: 60 }}>T 4%</TableCell>
                                <TableCell sx={{ minWidth: 60 }}>T 5%</TableCell>
                                <TableCell sx={{ minWidth: 80 }}>
                                    <TableSortLabel
                                        active={orderBy === 'torsionalStiffness'}
                                        direction={orderBy === 'torsionalStiffness' ? order : 'asc'}
                                        onClick={() => handleRequestSort('torsionalStiffness')}>
                                        Torsion
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ minWidth: 80 }}>
                                    <TableSortLabel active={orderBy === 'lcrEquationM2'} direction={orderBy === 'lcrEquationM2' ? order : 'asc'} onClick={() => handleRequestSort('lcrEquationM2')}>
                                        LCR M2
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ minWidth: 80 }}>
                                    <TableSortLabel active={orderBy === 'lcrEquationM1'} direction={orderBy === 'lcrEquationM1' ? order : 'asc'} onClick={() => handleRequestSort('lcrEquationM1')}>
                                        LCR M1
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ minWidth: 70 }}>
                                    <TableSortLabel active={orderBy === 'lcrEquationC'} direction={orderBy === 'lcrEquationC' ? order : 'asc'} onClick={() => handleRequestSort('lcrEquationC')}>
                                        LCR C
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ minWidth: 70 }}>
                                    <TableSortLabel
                                        active={orderBy === 'lcrEquationThreshold'}
                                        direction={orderBy === 'lcrEquationThreshold' ? order : 'asc'}
                                        onClick={() => handleRequestSort('lcrEquationThreshold')}>
                                        LCR Thr
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={{ minWidth: 150 }}>Projects</TableCell>
                            </TableRow>
                        </StyledTableHead>
                        <TableBody>
                            {paginatedProducts.map(product => (
                                <StyledTableRow key={product.id} editing={editingId === product.id ? 1 : 0}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            {editingId === product.id ? (
                                                <>
                                                    <Tooltip title="Save">
                                                        <IconButton size="small" onClick={handleSaveEdit} color="primary" disabled={loading}>
                                                            <SaveIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Cancel">
                                                        <IconButton size="small" onClick={handleCancelEdit} color="default">
                                                            <CancelIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <>
                                                    <Tooltip title="Edit">
                                                        <IconButton size="small" onClick={() => handleStartEdit(product)} disabled={loading || editingId !== null}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton size="small" onClick={() => handleDelete(product.id)} color="error" disabled={loading || editingId !== null}>
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.7rem', color: '#666' }}>{product.id}</TableCell>
                                    <EditableCell>{renderCell(product, 'prodotto', 'text')}</EditableCell>
                                    <EditableCell>{renderCell(product, 'family', 'text')}</EditableCell>
                                    <TableCell>{product.category}</TableCell>
                                    <EditableCell>{renderCell(product, 'tensile1Percent', 'number')}</EditableCell>
                                    <EditableCell>{renderCell(product, 'tensile2Percent', 'number')}</EditableCell>
                                    <EditableCell>{renderCell(product, 'tensile3Percent', 'number')}</EditableCell>
                                    <EditableCell>{renderCell(product, 'tensile4Percent', 'number')}</EditableCell>
                                    <EditableCell>{renderCell(product, 'tensile5Percent', 'number')}</EditableCell>
                                    <EditableCell>{renderCell(product, 'torsionalStiffness', 'number')}</EditableCell>
                                    <EditableCell>{renderCell(product, 'lcrEquationM2', 'number', 7, 0.0000001)}</EditableCell>
                                    <EditableCell>{renderCell(product, 'lcrEquationM1', 'number', 6, 0.000001)}</EditableCell>
                                    <EditableCell>{renderCell(product, 'lcrEquationC', 'number', 4, 0.0001)}</EditableCell>
                                    <EditableCell>{renderCell(product, 'lcrEquationThreshold', 'number', 0, 1)}</EditableCell>
                                    <TableCell>
                                        {editingId === product.id ? renderProjectsEditor(editedProduct.applicableProjects ?? []) : renderProjectChipsView(product.applicableProjects)}
                                    </TableCell>
                                </StyledTableRow>
                            ))}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    component="div"
                    count={products.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={e => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>
        </Box>
    );
};
